import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Camera, Trash2, Image as ImageIcon } from "lucide-react";
import { useLocalStorage } from "../hooks/useLocalStorage";
import UploadPhotos from "../components/UploadPhotos";
import { analyzePhotos } from "../lib/analyze";

type Risco = "Baixo" | "Médio" | "Alto";
type DemoItem = {
  id: string;
  marca: string;
  modelo: string;
  ano: number | "";
  fipe: number;
  dano: number;
  alvo: number;
  min: number;
  max: number;
  risco: Risco;
  createdAt: number;
  origem: "manual" | "fotos";
};

const fmtBRL = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(isFinite(n) ? n : 0);

function mascaraBRL(input: string) {
  const digits = input.replace(/\D/g, "");
  const val = Number(digits) / 100;
  return { valor: val, exibicao: fmtBRL(val) };
}

function calcularFaixa(fipe: number, dano: number) {
  const desconto = Math.min(0.3, 0.3 * (dano / 100)); // até 30% (→ 70% da FIPE)
  const alvo = fipe * (1 - desconto);
  const faixa = 0.05 * fipe; // ±5% da FIPE
  const min = Math.max(0.7 * fipe, alvo - faixa);
  const max = Math.min(1.0 * fipe, alvo + faixa);
  let risco: Risco = desconto > 0.2 ? "Alto" : desconto > 0.1 ? "Médio" : "Baixo";
  return { alvo, min, max, risco, desconto };
}

export default function Demo() {
  // formulário
  const [marca, setMarca] = useState("");
  const [modelo, setModelo] = useState("");
  const [ano, setAno] = useState<number | "">("");
  const [fipeMask, setFipeMask] = useState("R$ 0,00");
  const fipeValor = useMemo(() => Number(fipeMask.replace(/\D/g, "")) / 100, [fipeMask]);
  const [dano, setDano] = useState(0);

  // modos/toggles
  const [modoUpload, setModoUpload] = useState(false);
  const [usarCamera, setUsarCamera] = useState(false);
  const [cameraErro, setCameraErro] = useState<string | null>(null);

  // câmera refs e fotos capturadas
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [cameraPhotos, setCameraPhotos] = useState<File[]>([]);
  const [cameraPreviews, setCameraPreviews] = useState<string[]>([]);
  const [analyzing, setAnalyzing] = useState(false);

  // resultado + histórico
  const [resultado, setResultado] = useState<{
    alvo: number;
    min: number;
    max: number;
    risco: Risco;
    desconto: number;
  } | null>(null);
  const [historico, setHistorico] = useLocalStorage<DemoItem[]>("autocar_demo_history", []);

  // Desliga câmera quando entrar no modoUpload
  useEffect(() => {
    if (modoUpload) setUsarCamera(false);
  }, [modoUpload]);

  // Lifecycle da câmera
  useEffect(() => {
    if (!usarCamera) {
      if (videoRef.current?.srcObject) {
        (videoRef.current.srcObject as MediaStream)?.getTracks().forEach((t) => t.stop());
        videoRef.current.srcObject = null;
      }
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancel) return;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setCameraErro(null);
      } catch {
        setUsarCamera(false);
        setCameraErro("Não foi possível acessar a câmera. Verifique permissões e se o site está em HTTPS.");
      }
    })();
    return () => {
      cancel = true;
      if (videoRef.current?.srcObject) {
        (videoRef.current.srcObject as MediaStream)?.getTracks().forEach((t) => t.stop());
        videoRef.current.srcObject = null;
      }
    };
  }, [usarCamera]);

  // Cleanup dos previews da câmera
  useEffect(() => {
    return () => {
      cameraPreviews.forEach((p) => URL.revokeObjectURL(p));
    };
  }, [cameraPreviews]);

  function onChangeFipe(e: React.ChangeEvent<HTMLInputElement>) {
    const { exibicao } = mascaraBRL(e.target.value);
    setFipeMask(exibicao);
  }

  function calcularManual() {
    if (!fipeValor || fipeValor <= 0) {
      setResultado(null);
      return;
    }
    const { alvo, min, max, risco, desconto } = calcularFaixa(fipeValor, dano);
    setResultado({ alvo, min, max, risco, desconto });
    salvarHistorico({ alvo, min, max, risco, desconto, danoUsado: dano, origem: "manual" });
  }

  // Captura um frame da câmera como foto
  function capturarFoto() {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `camera-${Date.now()}.jpg`, { type: "image/jpeg" });
        const url = URL.createObjectURL(file);
        setCameraPhotos((prev) => [...prev, file]);
        setCameraPreviews((prev) => [...prev, url]);
      },
      "image/jpeg",
      0.95,
    );
  }

  function limparFotosCamera() {
    cameraPreviews.forEach((p) => URL.revokeObjectURL(p));
    setCameraPhotos([]);
    setCameraPreviews([]);
  }

  async function analisarFotosCamera() {
    if (cameraPhotos.length === 0) return;
    setAnalyzing(true);
    try {
      const data = await analyzePhotos({
        files: cameraPhotos,
        fields: {
          marca,
          modelo,
          ano: ano ? String(ano) : "",
          fipe: fipeValor ? String(fipeValor) : "0",
          danoSimulado: String(dano),
        },
      });
      handleAnalyzeResult(data);
    } catch (e: any) {
      alert(e?.message || "Erro ao analisar fotos.");
    } finally {
      setAnalyzing(false);
    }
  }

  function handleAnalyzeResult(data: any) {
    let m = data.marca ?? marca;
    let mo = data.modelo ?? modelo;
    let a = data.ano ?? (ano || new Date().getFullYear());
    let fipe = data.fipe ? Number(data.fipe) : fipeValor;

    let alvo = data.alvo;
    let min = data.min;
    let max = data.max;
    let risco = (data.risco as Risco) || "Baixo";
    let danoEstimado = typeof data.danoEstimado === "number" ? data.danoEstimado : dano;

    if (!alvo || !min || !max) {
      const calculado = calcularFaixa(fipe, danoEstimado);
      alvo = calculado.alvo;
      min = calculado.min;
      max = calculado.max;
      risco = calculado.risco;
    }

    const desconto = (fipe - alvo) / (fipe || 1);
    setResultado({ alvo, min, max, risco, desconto });

    setMarca(m);
    setModelo(mo);
    setAno(a);
    setFipeMask(fmtBRL(fipe));

    salvarHistorico({ alvo, min, max, risco, desconto, danoUsado: danoEstimado, origem: "fotos" });
  }

  function salvarHistorico({
    alvo,
    min,
    max,
    risco,
    desconto,
    danoUsado,
    origem,
  }: {
    alvo: number;
    min: number;
    max: number;
    risco: Risco;
    desconto: number;
    danoUsado: number;
    origem: "manual" | "fotos";
  }) {
    setHistorico((old) =>
      [
        {
          id: crypto.randomUUID(),
          marca: marca.trim() || "—",
          modelo: modelo.trim() || "—",
          ano: ano || new Date().getFullYear(),
          fipe: fipeValor,
          dano: danoUsado,
          alvo,
          min,
          max,
          risco,
          createdAt: Date.now(),
          origem,
        },
        ...old,
      ].slice(0, 30),
    );
  }

  return (
    <section className="pt-24 md:pt-28 pb-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-heading font-bold">Demo Interativa</h1>
          <p className="text-muted-foreground mt-2">
            Ajuste o <b>dano</b> ou envie <b>fotos</b> (upload/câmera) e veja o preço-alvo entre <b>70% e 100%</b> da
            FIPE.
          </p>
          {cameraErro && (
            <div className="mt-3 rounded-md border border-rose-300/40 bg-rose-500/10 text-rose-600 px-3 py-2 text-sm">
              {cameraErro}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form + Resultado */}
          <div className="lg:col-span-2 space-y-8">
            <div className="rounded-xl border bg-card p-6 shadow-sm">
              <h2 className="font-heading font-semibold text-lg mb-4">Parâmetros</h2>

              <div className="flex flex-wrap gap-3 mb-4">
                <Button
                  variant={modoUpload ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => setModoUpload((v) => !v)}
                  className="inline-flex items-center gap-2"
                >
                  <ImageIcon className="w-4 h-4" />
                  {modoUpload ? "Modo Fotos" : "Usar Fotos (upload)"}
                </Button>
                <Button
                  variant={usarCamera ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => setUsarCamera((v) => !v)}
                  disabled={modoUpload}
                  className="inline-flex items-center gap-2"
                >
                  <Camera className="w-4 h-4" />
                  {usarCamera ? "Câmera Ligada" : "Usar Câmera"}
                </Button>
              </div>

              {/* Modo manual (sem upload) */}
              {!modoUpload && (
                <>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-muted-foreground mb-1">Marca</label>
                      <input
                        className="w-full rounded-md border bg-background px-3 py-2"
                        placeholder="Ex: Honda"
                        value={marca}
                        onChange={(e) => setMarca(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-muted-foreground mb-1">Modelo</label>
                      <input
                        className="w-full rounded-md border bg-background px-3 py-2"
                        placeholder="Ex: Civic EX"
                        value={modelo}
                        onChange={(e) => setModelo(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-muted-foreground mb-1">Ano</label>
                      <input
                        type="number"
                        min={1980}
                        max={new Date().getFullYear() + 1}
                        className="w-full rounded-md border bg-background px-3 py-2"
                        placeholder="Ex: 2019"
                        value={ano}
                        onChange={(e) => setAno(e.target.value ? +e.target.value : "")}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-muted-foreground mb-1">Valor FIPE (R$)</label>
                      <input
                        className="w-full rounded-md border bg-background px-3 py-2"
                        inputMode="numeric"
                        value={fipeMask}
                        onChange={onChangeFipe}
                      />
                    </div>
                  </div>

                  <div className="mt-5">
                    <label className="block text-sm text-muted-foreground mb-1">Nível de danos (simulado)</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={dano}
                        onChange={(e) => setDano(+e.target.value)}
                        className="w-full"
                      />
                      <Badge variant="secondary">{dano}%</Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      0% = perfeito • 100% = muito avariado (desconto máximo aplicado: 30% da FIPE).
                    </p>
                  </div>
                </>
              )}

              {/* Câmera: captura e análise de fotos */}
              {!modoUpload && usarCamera && (
                <div className="mt-6 space-y-3">
                  <div className="relative rounded-lg overflow-hidden bg-black/60 aspect-video">
                    <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted />
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/5 to-transparent animate-[scan_3s_ease-in-out_infinite]" />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Button size="sm" onClick={capturarFoto}>
                      Capturar foto
                    </Button>
                    <Button size="sm" variant="outline" onClick={limparFotosCamera} disabled={!cameraPhotos.length}>
                      Limpar fotos
                    </Button>
                    <Button size="sm" onClick={analisarFotosCamera} disabled={!cameraPhotos.length || analyzing}>
                      {analyzing ? "Analisando..." : "Analisar fotos da câmera"}
                    </Button>
                  </div>

                  {cameraPreviews.length > 0 && (
                    <div className="grid grid-cols-4 gap-2">
                      {cameraPreviews.map((p, i) => (
                        <div key={i} className="w-full h-20 rounded overflow-hidden border bg-black/5">
                          <img src={p} alt={`camera-${i}`} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Upload de fotos quando modoUpload for ativo */}
              {modoUpload && (
                <div className="mt-6">
                  <UploadPhotos
                    onResult={handleAnalyzeResult}
                    initial={{ marca, modelo, ano: ano ? String(ano) : "", fipe: fipeValor ? String(fipeValor) : "" }}
                    maxFiles={8}
                    maxFileSizeMB={5}
                  />
                  <p className="mt-2 text-xs text-muted-foreground">Envie fotos do veículo para gerar estimativa.</p>
                </div>
              )}

              {!modoUpload && (
                <div className="mt-6">
                  <Button size="lg" className="w-full" onClick={calcularManual}>
                    Calcular Preço-Alvo
                  </Button>
                </div>
              )}
            </div>

            <Resultado fipeValor={fipeValor} resultado={resultado} />
          </div>

          {/* Histórico */}
          <Historico historico={historico} onClear={() => setHistorico([])} />
        </div>
      </div>

      {/* keyframe do efeito de "scanner" */}
      <style>{`@keyframes scan{0%{transform:translateY(-100%)}50%{transform:translateY(0%)}100%{transform:translateY(100%)}}`}</style>
    </section>
  );
}

function Resultado({
  fipeValor,
  resultado,
}: {
  fipeValor: number;
  resultado: { alvo: number; min: number; max: number; risco: Risco; desconto: number } | null;
}) {
  return (
    <div className="rounded-xl border bg-card p-6 shadow-sm">
      <h2 className="font-heading font-semibold text-lg mb-4">Resultado</h2>
      {!resultado ? (
        <p className="text-muted-foreground">
          Preencha os campos (modo manual) ou envie fotos para ver a faixa sugerida (70%–100% da FIPE).
        </p>
      ) : (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <Box titulo="Preço-alvo" valor={fmtBRL(resultado.alvo)} big />
            <Box titulo="Faixa sugerida" valor={`${fmtBRL(resultado.min)} – ${fmtBRL(resultado.max)}`} />
            <div className="rounded-md border p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Risco</p>
                <Badge
                  className={
                    resultado.risco === "Baixo"
                      ? "bg-emerald-500/15 text-emerald-600"
                      : resultado.risco === "Médio"
                        ? "bg-amber-500/15 text-amber-600"
                        : "bg-rose-500/15 text-rose-600"
                  }
                >
                  {resultado.risco}
                </Badge>
              </div>
              <span className="text-xs text-muted-foreground">
                desconto ~ {Math.round(resultado.desconto * 1000) / 10}%
              </span>
            </div>
          </div>

          <div className="rounded-md border p-4">
            <p className="text-xs text-muted-foreground mb-3">Comparativo</p>
            <div className="grid gap-3">
              <Bar label="FIPE" value={fipeValor} base={fipeValor} />
              <Bar label="Máx (100%)" value={Math.min(fipeValor, resultado.max)} base={fipeValor} />
              <Bar label="Alvo" value={resultado.alvo} base={fipeValor} highlight />
              <Bar label="Mín (70%)" value={Math.max(0.7 * fipeValor, resultado.min)} base={fipeValor} />
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            *Cálculo ilustrativo. A versão com IA pode estimar automaticamente o “dano” via fotos/câmera para compor a
            faixa 70–100% da FIPE.
          </p>
        </div>
      )}
    </div>
  );
}

function Box({ titulo, valor, big = false }: { titulo: string; valor: string; big?: boolean }) {
  return (
    <div className="rounded-md border p-4">
      <p className="text-xs text-muted-foreground mb-1">{titulo}</p>
      <p className={`${big ? "text-2xl" : "text-xl"} font-heading font-bold`}>{valor}</p>
    </div>
  );
}

function Bar({
  label,
  value,
  base,
  highlight = false,
}: {
  label: string;
  value: number;
  base: number;
  highlight?: boolean;
}) {
  const pct = Math.max(0, Math.min(1, value / Math.max(1, base)));
  return (
    <div>
      <div className="flex items-center justify-between mb-1 text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{fmtBRL(value)}</span>
      </div>
      <div className="h-2 w-full rounded bg-muted/60 overflow-hidden">
        <div className={`h-full ${highlight ? "bg-primary" : "bg-primary/60"}`} style={{ width: `${pct * 100}%` }} />
      </div>
    </div>
  );
}

function Historico({ historico, onClear }: { historico: DemoItem[]; onClear: () => void }) {
  return (
    <aside className="space-y-4">
      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-semibold text-lg">Histórico</h2>
          {historico.length > 0 && (
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={onClear}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
        {historico.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma análise salva ainda.</p>
        ) : (
          <ul className="space-y-3">
            {historico.map((h) => (
              <li key={h.id} className="rounded-md border p-3 text-xs hover:bg-accent/5">
                <div className="flex items-center justify-between">
                  <span className="font-medium">
                    {h.marca} {h.modelo} {h.ano || ""}
                  </span>
                  <span className="text-muted-foreground">
                    {new Date(h.createdAt).toLocaleDateString("pt-BR")} • {h.origem === "fotos" ? "fotos" : "manual"}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-muted-foreground">
                    FIPE {fmtBRL(h.fipe)} • dano {h.dano}%
                  </span>
                  <span className="font-semibold">{fmtBRL(h.alvo)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-xl border bg-card p-4 text-xs text-muted-foreground">
        <p className="mb-1 font-medium text-foreground">Como funciona?</p>
        <p>
          No modo manual você ajusta o <b>slider de danos</b>. No modo fotos (upload/câmera), o backend pode estimar
          automaticamente o dano para compor a faixa entre <b>70% e 100%</b> da FIPE.
        </p>
      </div>
    </aside>
  );
}
